import { memo, useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";

// Sticky corner boombox — bottom-left of every page (Bree is bottom-right).
// Swipe LEFT (or drag mouse left) to dismiss for the session; reload brings
// it back. No persistent localStorage flag — the "reload to restore" is the
// intended UX.
const AUDIO_SRC = "https://customer-assets-m6fa6gv7.emergentagent.net/job_127be0a7-455f-4886-b271-789f0b3e698a/artifacts/76qrf13b_booty-breeze.mp3";
const SWIPE_DISMISS_PX = 80;   // horizontal distance (left) that triggers dismiss

function BoomboxAirFreshenerInner() {
  const [playing, setPlaying] = useState(false);
  const [primed, setPrimed] = useState(false);
  const [muted, setMuted] = useState(true);   // starts muted so autoplay lands on every browser
  const [dismissed, setDismissed] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [releasing, setReleasing] = useState(false);   // toggles CSS transition on release
  const [amp, setAmp] = useState(0);
  const [beatIdx, setBeatIdx] = useState(0);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const beatTimerRef = useRef(null);
  const wantsToPlayRef = useRef(false);
  const mutedRef = useRef(true);              // ref mirror for animation loop
  // Swipe tracking
  const dragStartRef = useRef(null);   // { x, y, dragging }

  const ensureAnalyser = () => {
    if (ctxRef.current) return;
    const a = audioRef.current;
    // Chrome poisons the audio graph if you connect createMediaElementSource
    // to a *muted* element — the analyser reads 0 forever, even after
    // unmuting. So we defer the graph until the media element is actually
    // audible.
    if (!a || a.muted) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaElementSource(a);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analyser.connect(ctx.destination);
      ctxRef.current = ctx;
      analyserRef.current = analyser;
    } catch (e) {
      // MediaElementSource can only be created once per element — safe to swallow
      console.warn("boombox: analyser init:", e?.message || e);
    }
  };

  const tick = () => {
    const analyser = analyserRef.current;
    // No analyser yet (we're still in muted-autoplay phase) OR we're muted →
    // drive the visualiser with a synthetic sine so the boombox looks alive.
    if (!analyser || mutedRef.current) {
      const t = performance.now();
      const beat = Math.max(0, Math.sin(t / 380)) * 0.55
                 + Math.max(0, Math.sin(t / 190)) * 0.25
                 + 0.12;
      setAmp(Math.min(1, beat));
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    const data = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(data);
    let sum = 0;
    const bins = Math.min(data.length, 20);
    for (let i = 0; i < bins; i++) sum += data[i];
    const real = sum / bins / 255;
    // Fallback: if the analyser is somehow silent while we believe we're
    // playing audibly, keep the sine pulse going so we never freeze.
    setAmp(real > 0.02 ? real : Math.max(0, Math.sin(performance.now() / 300)) * 0.35 + 0.1);
    rafRef.current = requestAnimationFrame(tick);
  };

  const startPlayback = async () => {
    const a = audioRef.current;
    if (!a) return false;
    ensureAnalyser();
    if (ctxRef.current?.state === "suspended") {
      try { await ctxRef.current.resume(); } catch { /* noop */ }
    }
    // Try to play — the promise may reject on strict autoplay policies even
    // when the HTML `autoplay` attribute already got the media element rolling.
    try { await a.play(); } catch (e) {
      console.debug("boombox: a.play() rejected:", e?.message || e);
    }
    // The source of truth is the *actual* paused state, not the promise result.
    if (!a.paused) {
      setPlaying(true);
      wantsToPlayRef.current = true;
      if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
      if (!beatTimerRef.current) {
        beatTimerRef.current = setInterval(() => setBeatIdx((i) => (i + 1) % 12), 125);
      }
      return true;
    }
    return false;
  };

  const pausePlayback = () => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    wantsToPlayRef.current = false;
    setPlaying(false);
    if (beatTimerRef.current) { clearInterval(beatTimerRef.current); beatTimerRef.current = null; }
  };

  const toggle = async () => {
    setPrimed(true);
    const a = audioRef.current;
    if (!a) return;
    // If we're muted (still in the pre-gesture phase), tapping the boombox
    // unmutes it rather than pausing — that's the natural "give me sound" intent.
    if (a.muted || mutedRef.current) {
      try {
        a.muted = false;
        mutedRef.current = false;
        setMuted(false);
        // NOW build the audio graph — safe because element is no longer muted.
        ensureAnalyser();
        if (ctxRef.current?.state === "suspended") await ctxRef.current.resume();
        if (a.paused) await startPlayback();
      } catch (e) {
        console.debug("boombox: tap-unmute failed:", e?.message || e);
      }
      return;
    }
    if (a.paused) await startPlayback();
    else pausePlayback();
  };

  // ---- Swipe-left to dismiss (touch + mouse) ----
  const onPointerDown = (e) => {
    dragStartRef.current = {
      x: e.clientX ?? e.touches?.[0]?.clientX ?? 0,
      y: e.clientY ?? e.touches?.[0]?.clientY ?? 0,
      moved: false,
    };
    setReleasing(false);
  };
  const onPointerMove = (e) => {
    const s = dragStartRef.current;
    if (!s) return;
    const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const cy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    const dx = cx - s.x;
    const dy = cy - s.y;
    // Only track LEFT drag — clamp positive to 0
    if (dx < 0 && Math.abs(dx) > Math.abs(dy)) {
      s.moved = true;
      setDragX(dx);
    }
  };
  const onPointerUp = () => {
    const s = dragStartRef.current;
    dragStartRef.current = null;
    if (!s) return;
    setReleasing(true);
    if (dragX <= -SWIPE_DISMISS_PX) {
      // Animate off-screen then unmount
      setDragX(-320);
      setTimeout(() => {
        if (playing) pausePlayback();
        setDismissed(true);   // in-memory only — reload brings it back
      }, 220);
    } else {
      setDragX(0);
      // If barely moved, treat as a tap
      if (!s.moved) toggle();
    }
  };

  // ---- MUTED AUTOPLAY on mount, then UNMUTE on first user gesture ----
  // Browsers universally allow muted autoplay, so we start the audio graph
  // muted (silent to the user, but the boombox is visually alive via a
  // synthetic pulse). On the very first click/tap/keypress anywhere on the
  // page we flip mute off and the real track roars in.
  useEffect(() => {
    if (dismissed) return;
    const a = audioRef.current;
    if (!a) return;

    // Kick off the muted play — this succeeds on Chrome/Safari/Firefox/mobile
    // without a prior gesture. If our own play() promise rejects, the HTML
    // `autoplay muted` attribute may still get the media element rolling, so
    // we poll for that too and flip `playing` on as soon as the element is
    // actually rolling.
    a.muted = true;
    mutedRef.current = true;
    setMuted(true);
    startPlayback().catch(() => { /* boombox stays visually alive either way */ });

    // Poll for a few seconds — the browser might start the audio *after* our
    // useEffect fires (autoplay attribute), and Chrome sometimes rejects our
    // explicit .play() call even when the element ends up rolling.
    let pollTicks = 0;
    const pollId = setInterval(() => {
      pollTicks++;
      if (!a.paused) {
        // Media element is actually playing — sync our state.
        setPlaying(true);
        wantsToPlayRef.current = true;
        if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
        if (!beatTimerRef.current) {
          beatTimerRef.current = setInterval(() => setBeatIdx((i) => (i + 1) % 12), 125);
        }
        clearInterval(pollId);
      } else if (pollTicks > 40) {   // ~4s ceiling
        clearInterval(pollId);
      }
    }, 100);

    let unmuted = false;
    const unmuteNow = async () => {
      if (unmuted) return;
      unmuted = true;
      try {
        a.muted = false;
        mutedRef.current = false;
        setMuted(false);
        setPrimed(true);
        // NOW build the audio graph — connecting a MediaElementSource to a
        // muted element poisons the analyser (Chrome reads 0 forever), so we
        // waited until after unmute to create it.
        ensureAnalyser();
        if (ctxRef.current?.state === "suspended") await ctxRef.current.resume();
        if (a.paused) await startPlayback();
      } catch (e) {
        console.debug("boombox: unmute failed:", e?.message || e);
      }
      ["pointerdown", "touchstart", "keydown", "scroll"].forEach((ev) =>
        window.removeEventListener(ev, unmuteNow, true)
      );
    };
    ["pointerdown", "touchstart", "keydown", "scroll"].forEach((ev) =>
      window.addEventListener(ev, unmuteNow, { capture: true, passive: true })
    );
    return () => {
      clearInterval(pollId);
      ["pointerdown", "touchstart", "keydown", "scroll"].forEach((ev) =>
        window.removeEventListener(ev, unmuteNow, true)
      );
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed]);

  // ---- Auto-resume if the browser pauses us while user wanted play ----
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPause = () => {
      // Only resume if user hasn't explicitly paused us
      if (wantsToPlayRef.current && a.paused && !a.ended) {
        a.play().catch(() => { /* browser policy — leave paused */ });
      }
    };
    const onEnd = () => {
      wantsToPlayRef.current = false;
      setPlaying(false);
      if (beatTimerRef.current) { clearInterval(beatTimerRef.current); beatTimerRef.current = null; }
    };
    const onPlay = () => setPlaying(true);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnd);
    a.addEventListener("play", onPlay);
    return () => {
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("play", onPlay);
    };
  }, []);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (beatTimerRef.current) clearInterval(beatTimerRef.current);
    if (ctxRef.current) ctxRef.current.close().catch(() => {});
  }, []);

  if (dismissed) return null;

  // Fade opacity as user drags — full fade at 160px left
  const opacity = Math.max(0, 1 - Math.abs(Math.min(0, dragX)) / 160);

  return (
    <div
      className="fixed bottom-5 left-5 z-40 select-none touch-pan-y"
      data-testid="boombox-sticky"
      style={{
        transform: `translateX(${dragX}px)`,
        opacity,
        transition: releasing ? "transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms" : "none",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div
        onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(); } }}
        role="button" tabIndex={0}
        aria-label={playing ? "Pause the Booty Breeze anthem" : "Play the Booty Breeze anthem"}
        className="relative cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-transform"
        data-testid="boombox-tap-target"
        style={{ width: 220 }}
      >
        <BoomboxSvg amp={amp} playing={playing} beatIdx={beatIdx} />

        {(!primed || muted) && (
          <div className="absolute -top-11 left-1/2 -translate-x-1/2 whitespace-nowrap" data-testid="boombox-hint">
            <div className="animate-bounce inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-[10px] shadow-lg shadow-[#FF3D8B]/50">
              <Play size={11} /> {muted ? "Tap for sound" : "Press play"}
            </div>
            <div className="w-0 h-0 mx-auto border-l-[6px] border-r-[6px] border-t-[7px] border-l-transparent border-r-transparent border-t-[#FF3D8B]" />
          </div>
        )}
      </div>

      <audio ref={audioRef} src={AUDIO_SRC} preload="auto" crossOrigin="anonymous"
             autoPlay muted playsInline loop data-testid="boombox-audio-el" />
    </div>
  );
}


// Memoize so the boombox doesn't re-render when parent contexts (Cart, Ads,
// Auth) push updates. Its own internal state still triggers renders as needed.
const BoomboxAirFreshener = memo(BoomboxAirFreshenerInner);
export default BoomboxAirFreshener;


// ---------- SVG boombox — only speaker <g> transforms scale ----------
function BoomboxSvg({ amp, playing, beatIdx }) {
  // Bigger, punchier speaker pump — up to 65% scale on peak bass hits
  const coneScale = playing ? 1 + amp * 0.65 : 1 + Math.sin(Date.now() / 700) * 0.02;
  // Fast strobe for the "on the beat" flashes
  const strobe = playing ? Math.sin(Date.now() / 70) * 0.5 + 0.5 : 0;
  // Bass-drop flash — when amp spikes past the threshold, briefly white-flash the chassis edge
  const isBassDrop = playing && amp > 0.55;
  // Each LED has its own phase — creates a proper wave that "breathes" instead of a hard chase.
  // Amplitude BOOSTS the LEDs, but the base sine gives them individual life.
  const t = Date.now();
  const ledBrightness = (i) => {
    if (!playing) return 0;
    const phase = (t / 220) + i * 0.55;   // each LED offset by 0.55 rad → visible wave
    const wave = (Math.sin(phase) + 1) / 2;  // 0..1
    // Blend the wave with the live amplitude so bass hits punch the whole strip up
    return Math.min(1, wave * 0.55 + amp * 0.75);
  };
  const vuLit = (i) => playing && amp >= 0.15 + i * 0.14;
  // Color palette — cycles per LED position
  const colors = ["#FF3D8B", "#FF7AB6", "#A855F7", "#5A9CFF", "#38E6C5", "#F9E040", "#FF9A3D",
                  "#FF3D8B", "#FF7AB6", "#A855F7", "#5A9CFF", "#38E6C5"];

  return (
    <svg viewBox="0 0 280 160" className="w-full h-auto drop-shadow-[0_10px_20px_rgba(0,0,0,0.55)]" overflow="visible">
      <defs>
        <linearGradient id="bx-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2a2a2a" />
          <stop offset="0.5" stopColor="#0e0e0e" />
          <stop offset="1" stopColor="#1a1a1a" />
        </linearGradient>
        <linearGradient id="bx-speaker" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#333" />
          <stop offset="1" stopColor="#111" />
        </linearGradient>
        <radialGradient id="bx-cone-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#FF3D8B" stopOpacity={playing ? 0.95 : 0} />
          <stop offset="0.65" stopColor="#A855F7" stopOpacity={playing ? 0.5 : 0} />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="bx-cone-halo-l" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#FF3D8B" stopOpacity={playing ? 0.5 + amp * 0.5 : 0} />
          <stop offset="1" stopColor="#FF3D8B" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="bx-cone-halo-r" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#A855F7" stopOpacity={playing ? 0.5 + amp * 0.5 : 0} />
          <stop offset="1" stopColor="#A855F7" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="bx-screen" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#0d0d0d" />
          <stop offset="0.5" stopColor="#1a1a1a" />
          <stop offset="1" stopColor="#0d0d0d" />
        </linearGradient>
      </defs>

      <rect x="105" y="6" width="70" height="8" rx="4" fill="#111" stroke="#333" strokeWidth="1" />

      <line x1="240" y1="14" x2="260" y2="0" stroke="#666" strokeWidth="2" strokeLinecap="round" />
      <circle cx="260" cy="0" r={playing ? 3 + amp * 3 : 2.5}
              fill="#FF3D8B" opacity={playing ? 0.5 + strobe * 0.5 : 0.35}
              style={{ filter: playing ? `drop-shadow(0 0 ${4 + amp * 8}px #FF3D8B)` : "none" }} />

      {/* Chassis — pulses stronger border on bass drops */}
      <rect x="8" y="18" width="264" height="132" rx="14" fill="url(#bx-body)"
            stroke={isBassDrop ? "#FF3D8B" : "#3a3a3a"}
            strokeWidth={isBassDrop ? 2.5 : 1.5}
            style={{ filter: isBassDrop ? `drop-shadow(0 0 8px #FF3D8B)` : "none" }} />

      {/* LED bar backing */}
      <rect x="14" y="24" width="252" height="12" rx="4" fill="#0a0a0a" stroke="#2a2a2a" />
      {/* Individually-breathing LEDs — wave up + wave down + amp punch */}
      {Array.from({ length: 12 }).map((_, i) => {
        const b = ledBrightness(i);
        const color = colors[i];
        const r = 2 + b * 2.5;   // 2 (dim) → 4.5 (bright)
        return (
          <circle key={i} cx={22 + i * 21} cy="30" r={r}
                  fill={b > 0.1 ? color : "#333"}
                  opacity={0.4 + b * 0.6}
                  style={{ filter: b > 0.3 ? `drop-shadow(0 0 ${b * 6}px ${color})` : "none" }} />
        );
      })}

      <rect x="90" y="42" width="100" height="18" rx="3" fill="url(#bx-screen)" stroke="#222" />
      <text x="140" y="55" textAnchor="middle" fontFamily="monospace" fontSize="11" fontWeight="700"
            fill={playing ? "#FF3D8B" : "#4a4a4a"} letterSpacing="1"
            style={{ filter: playing && amp > 0.35 ? "drop-shadow(0 0 4px #FF3D8B)" : "none" }}>
        {playing ? (amp > 0.35 ? "▮▮▮▮▮" : amp > 0.2 ? "▮▮▮▯▯" : "▮▮▯▯▯") : "PRESS ►"}
      </text>
      <circle cx="30" cy="51" r="3" fill={playing ? "#FF3D8B" : "#3a3a3a"} opacity={playing ? 0.5 + strobe * 0.5 : 0.4} />
      <circle cx="45" cy="51" r="3" fill={playing ? "#A855F7" : "#3a3a3a"} opacity={playing ? 0.5 + (1 - strobe) * 0.5 : 0.4} />
      <circle cx="240" cy="51" r="3" fill={playing ? "#FF3D8B" : "#3a3a3a"} opacity={playing ? amp + 0.25 : 0.4} />
      <circle cx="255" cy="51" r="3" fill={playing ? "#A855F7" : "#3a3a3a"} opacity={playing ? 0.55 : 0.4} />

      {Array.from({ length: 5 }).map((_, i) => {
        const lit = vuLit(i);
        const y = 132 - i * 8;
        const color = i < 2 ? "#38E6C5" : i < 4 ? "#F9E040" : "#FF3D8B";
        return (
          <rect key={`vu-l-${i}`} x="102" y={y} width="7" height="6" rx="1"
                fill={lit ? color : "#222"} opacity={lit ? 1 : 0.5}
                style={{ filter: lit ? `drop-shadow(0 0 4px ${color})` : "none" }} />
        );
      })}
      {Array.from({ length: 5 }).map((_, i) => {
        const lit = vuLit(i);
        const y = 132 - i * 8;
        const color = i < 2 ? "#38E6C5" : i < 4 ? "#F9E040" : "#A855F7";
        return (
          <rect key={`vu-r-${i}`} x="171" y={y} width="7" height="6" rx="1"
                fill={lit ? color : "#222"} opacity={lit ? 1 : 0.5}
                style={{ filter: lit ? `drop-shadow(0 0 4px ${color})` : "none" }} />
        );
      })}

      {/* LEFT SPEAKER — soft halo behind the cone that expands on bass hits */}
      <circle cx="58" cy="108" r={44 + amp * 22} fill="url(#bx-cone-halo-l)" />
      <g transform={`translate(58, 108) scale(${coneScale})`}>
        <circle r="34" fill="#0a0a0a" stroke="#2a2a2a" strokeWidth="1.5" />
        <circle r="32" fill="url(#bx-speaker)" />
        <circle r="26" fill="url(#bx-cone-glow)" opacity={playing ? 1 : 0} />
        <circle r="26" fill="none" stroke="#FF3D8B" strokeWidth="1.5" opacity={playing ? 0.55 + amp * 0.45 : 0} />
        <circle r="18" fill="none" stroke="#A855F7" strokeWidth="1.5" opacity={playing ? 0.55 + amp * 0.45 : 0} />
        <circle r="11" fill="#111" stroke="#333" />
        <circle r={4 + amp * 2} fill={playing ? "#FF3D8B" : "#2a2a2a"}
                style={{ filter: playing && amp > 0.3 ? "drop-shadow(0 0 3px #FF3D8B)" : "none" }} />
      </g>

      {/* RIGHT SPEAKER */}
      <circle cx="222" cy="108" r={44 + amp * 22} fill="url(#bx-cone-halo-r)" />
      <g transform={`translate(222, 108) scale(${coneScale})`}>
        <circle r="34" fill="#0a0a0a" stroke="#2a2a2a" strokeWidth="1.5" />
        <circle r="32" fill="url(#bx-speaker)" />
        <circle r="26" fill="url(#bx-cone-glow)" opacity={playing ? 1 : 0} />
        <circle r="26" fill="none" stroke="#A855F7" strokeWidth="1.5" opacity={playing ? 0.55 + amp * 0.45 : 0} />
        <circle r="18" fill="none" stroke="#FF3D8B" strokeWidth="1.5" opacity={playing ? 0.55 + amp * 0.45 : 0} />
        <circle r="11" fill="#111" stroke="#333" />
        <circle r={4 + amp * 2} fill={playing ? "#A855F7" : "#2a2a2a"}
                style={{ filter: playing && amp > 0.3 ? "drop-shadow(0 0 3px #A855F7)" : "none" }} />
      </g>

      <rect x="122" y="80" width="36" height="50" rx="3" fill="#0d0d0d" stroke="#2a2a2a" strokeWidth="1.5" />
      <rect x="126" y="85" width="28" height="40" rx="2" fill="#1a1a1a" />
      <circle cx="132" cy="105" r="5" fill="#222" stroke="#3a3a3a" />
      <circle cx="132" cy="105" r="2" fill="#0a0a0a" />
      <circle cx="148" cy="105" r="5" fill="#222" stroke="#3a3a3a" />
      <circle cx="148" cy="105" r="2" fill="#0a0a0a" />
      <rect x="128" y="88" width="24" height="6" rx="1" fill="#0a0a0a" />
      <text x="140" y="93" textAnchor="middle" fontSize="4" fill="#FF3D8B" fontFamily="sans-serif" fontWeight="700" letterSpacing="0.5">B·BREEZE</text>

      <g transform="translate(140, 143)">
        <circle r={9 + (playing ? amp * 3 : Math.sin(Date.now() / 400) * 1.5)}
                fill={playing ? (isBassDrop ? "#FF3D8B" : "#A855F7") : "#FF3D8B"}
                opacity={playing ? 0.95 : 0.9}
                style={{ filter: `drop-shadow(0 0 ${playing ? 8 + amp * 16 : 4}px ${playing ? "#A855F7" : "#FF3D8B"})` }} />
        {playing ? (
          <g fill="#000">
            <rect x="-3.5" y="-4" width="2.5" height="8" rx="1" />
            <rect x="1" y="-4" width="2.5" height="8" rx="1" />
          </g>
        ) : (
          <path d="M -3 -4 L 4 0 L -3 4 Z" fill="#000" />
        )}
      </g>
    </svg>
  );
}
